/**
 * certificatePrefill — what a cover period read off a certificate does to the two date fields,
 * and the one sentence that says so.
 *
 * THE CERTIFICATE IS THE AUTHORITY, SO A CONFIDENT READ OVERWRITES. The complaint this feature
 * answers is mis-ENTERED cover dates, and every policy in production already carries both dates:
 * a fill-only-when-empty rule would therefore stand down in exactly the case it exists for. A
 * `found` read — an isolated, unambiguous PAIR, never one date alone — lands on both fields the
 * moment it resolves, whatever they held, and the line under the certificate row names the period
 * it wrote and offers the one click back. No confirm, no Apply button: the owner asked for an
 * automatic prefill and the house rule forbids friction nobody asked for.
 *
 * AND ONLY A VERIFIED CERTIFICATE GETS THAT FAR. `decideCertificate` passes exactly one thing —
 * a statutory certificate that prints an identifier agreeing with this contract's vehicle — and
 * turns everything else into a `rejected` read carrying the reason: a document nobody could read,
 * an image where a PDF was wanted, a file over the reader's ceiling, a file that is not a
 * Certificate of Insurance at all, one printing neither identifier, a vehicle record with nothing
 * to check it against, and the one that names another car. The rule used to
 * refuse only the last of those (owner's ruling, 2026-09-12, reversing it: a certificate that
 * cannot be checked is not evidence of cover). THE REJECTION IS THE CERTIFICATE'S AND NOT THE
 * POLICY'S: no dates are prefilled from a rejected read, the dates already in the fields are left
 * exactly as they are, and `validatePolicyDraft` is untouched — a refusal there would lock every
 * save of a policy whose `vehicles.vin` is the wrong side of a disagreement, which on live data is
 * two mismatches out of three. See `decideCertificate` and `certificateVehicle`.
 *
 * A CERTIFICATE ALREADY ON FILE WARNS INSTEAD OF REFUSING, AND THE DIFFERENCE IS THAT IT IS FILED.
 * `certificateRefusal`'s second argument is the whole of it: a document being CHOSEN is stopped
 * before it reaches storage and told so, and one already in storage is told what is wrong with it
 * and asked for the insurer's PDF against this policy. A sentence cannot un-file a document, and
 * one claiming to would be read as noise.
 *
 * NOTHING HERE WRITES TO THE DATABASE. The decision produces field sets for the caller to dispatch
 * as ordinary edits; the two canvases' CommitBars remain the only writers.
 *
 * TWO DOORS, ONE SET OF RULES. A certificate reaches a read either by being STAGED for upload or by
 * being FETCHED BACK DOWN from storage, which since 2026-09-12 happens automatically on every
 * policy view and is how the certificates already filed get checked without re-uploading them.
 * Nothing in this module knows which door a read came through, and that is the point: `readFor`
 * and `staged` are compared by object identity, so the caller hands in whichever File the read
 * speaks for and every rule below applies unchanged.
 *
 * WHAT THE SECOND DOOR NO LONGER DOES IS APPLY ANYTHING. An automatic read publishes a state with
 * `mode: null`, so `prefillLine` writes it as a STANDING FACT carrying the one click that moves the
 * dates, and `decidePrefill` — overwrite, hold, drop — governs the staged door alone. The rules are
 * unchanged; the on-file surfaces simply stopped calling them.
 *
 * THE THREE "NEVER"s ARE THE REDUCER'S, NOT TASTE. A read that resolves mid-commit must be HELD
 * (applying it would knock the group out of `saving` and re-enable Save over a row-creating write);
 * a read whose file is no longer staged must be DROPPED; and a read that resolves after a commit
 * landed must be dropped too, because the file was filed with the dates as they were typed. Rule
 * order is the contract — `decidePrefill` checks staleness BEFORE it checks `saving`, so a read
 * that is simultaneously stale and mid-commit dies rather than waiting for a group it no longer
 * belongs to.
 *
 * PURE, AND REACT-FREE. Nothing here imports `@/lib/sbt-pdf` — its `?url` worker specifier is
 * Vite-only and vitest's node env cannot resolve it, not even through a type import — and nothing
 * here formats a date, or builds one: the period text and the single-date text arrive as injected
 * functions so that both surfaces print them through `policyPeriodText`'s convention, on the Ghana
 * calendar day, and today arrives as a 'yyyy-MM-dd' STRING rather than a Date. This module compares
 * two such strings lexically — which is exactly a calendar-day comparison for a fixed-width ISO
 * date — and so needs neither `date-fns` nor a clock of its own, and stays testable.
 */

import type { CoverPeriodReason } from './certificateDates';
import {
    matchCertificateVehicle,
    normaliseIdentifier,
    vehicleRefusal,
    type CertificateVehicle,
    type ContractVehicle,
    type VehicleMatch,
} from './certificateVehicle';

/** The two cover fields, exactly as the inputs hold them: 'yyyy-MM-dd', or '' for empty. */
export interface DatePair {
    startsAt: string;
    expiresAt: string;
}

/**
 * Why no period came back. `none`/`ambiguous` are the parser's; the rest are the read path's.
 *
 * THERE IS NO FETCH REASON HERE, AND THAT IS DELIBERATE. The certificate already on file is read
 * through a react-query entry (`readFiledCertificate`), so a fetch that never landed arrives at the
 * screen as `isError` and never as a `CoverRead` — the surfaces print `FETCH_FAILED_NOTE` straight.
 * A 'fetch-failed' member here would be a reason nothing can construct and every branch handling it
 * would be dead code claiming to be reachable.
 */
export type UnreadableReason =
    | 'none' // the parser found no cover-period label it trusts
    | 'ambiguous' // it found candidates it cannot choose between
    | 'no-text' // a PDF with no text layer, and OCR was not allowed to spend a pass on it
    | 'not-pdf' // the staged file is an image or an office document
    | 'failed' // pdfjs threw: encrypted, corrupt, or not the file it claims to be
    | 'too-large' // refused before loading pdfjs, to keep the tab responsive
    /*
     * THE FOUR WAYS THE SCAN BRANCH ENDS WITHOUT TEXT, and they are four rather than one because
     * the breadcrumb is the only place anyone learns what OCR actually does in the field. To the
     * ADMIN they are one fact — `rejectionFor` maps all four onto the same 'unreadable' refusal,
     * which is what carries the attested fallback onto them — but to whoever is deciding whether
     * the 20-second ceiling is too tight, a timeout and a worker that never started are opposite
     * findings.
     */
    | 'ocr-no-text' // the pass ran and the page came back as marks rather than words
    | 'ocr-timeout' // the whole-read ceiling was reached
    | 'ocr-failed' // the chunk, the worker or the canvas would not come up
    | 'ocr-too-many-pages'; // more pages than may be read whole, so none of it is read

/**
 * WHY A DOCUMENT IS NOT THIS CONTRACT'S COVER, in the seven shapes the rule can take.
 *
 * 'mismatch' is the one the vehicle rule raises about a document it could read in full; the other
 * six are the ways a certificate arrives that NOBODY CAN CHECK — which the owner ruled on
 * 2026-09-12 are refusals too, not remarks. See `decideCertificate`.
 *
 * 'not-pdf' AND 'too-large' ARE THEIR OWN REASONS AND NOT FOLDED INTO 'unreadable', because they
 * are the two refusals an admin can act on WITHOUT going back to the insurer: the file in the
 * picker is an image of the certificate, or the right document saved at too high a resolution. The
 * generic sentence would send them hunting for a fault in paper that is perfectly good.
 */
export type RejectionReason =
    | 'unreadable' // nothing could be read off it at all (a scan, a corrupt PDF)
    | 'not-pdf' // an image or an office document was chosen, so there is no text layer at all
    | 'too-large' // over the reader's ceiling, so the parse was never attempted
    | 'not-statutory' // it reads, and it is not a Certificate of Insurance
    | 'no-identifier' // it reads, and prints neither a registration nor a chassis number
    | 'vehicle-unrecorded' // it prints one, and the vehicle record has nothing to meet it with
    | 'mismatch'; // both printed, and they disagree

/** What the read of one staged file came to. */
export type CoverRead =
    /**
     * `phase` IS THE ONE THING A WAIT HAS TO SAY, AND ONLY ONE WAIT HAS IT. A text layer is read in
     * milliseconds; a scan is rendered, binarised and put through a wasm OCR pass, and the admin
     * watching the row has no way to tell a slow read from a stuck one. So the moment the reader
     * finds itself on that branch it says so, and the sentence changes from 'Reading the
     * certificate…' to one that names what is happening and why it is taking seconds.
     */
    | { status: 'reading'; phase?: 'ocr' }
    /**
     * `ocr` IS PROVENANCE AND IT CHANGES TWO THINGS. `decideCertificate` will not accept a read off
     * a scan unless the PLATE matched — a chassis-only agreement is not enough when the identifiers
     * were guessed at from pixels — and `prefillLine` says on the screen that the period came off a
     * scan and asks the admin to check it against the document. Absent on every read off a text
     * layer, which is 39 of the 41 certificates filed in production.
     */
    | {
          status: 'found';
          startsOn: string;
          expiresOn: string;
          vehicle?: CertificateVehicle;
          ocr?: true;
      }
    /**
     * THE DOCUMENT CANNOT BE FILED AGAINST THIS CONTRACT, AND `reason` SAYS WHY. Where there was
     * a period to read it is carried, so the state is a complete record of what the read said, and
     * it is DELIBERATELY NOT APPLIED; where there was a comparison to make, `match` carries it so
     * the mismatch sentence can name the two values. Both are absent on the four endings that never
     * got as far as a period — 'unreadable', 'not-pdf', 'too-large' and 'not-statutory' — see
     * `decideCertificate`.
     *
     * AND `unreadable` CARRIES THE RAW ENDING THE REFUSAL WAS FOLDED FROM, WHICH IS THE ONE THING
     * THE DECISION USED TO THROW AWAY. `rejectionFor` folds four different endings into
     * 'unreadable' — a scan with no text layer, a pdfjs throw, and two parser verdicts given over
     * a text layer it DID read — and one sentence is right for all four. But a rule that turns on
     * "nobody could read this" as opposed to "something read it and could not pin the period" then
     * has nothing left to ask, and `attestable` is exactly that rule: the door it opens may only be
     * opened on an ending where the document was never read at all. So the ending travels with the
     * refusal. It is set only on the unreadable branch and is absent on every other reason.
     *
     * AND `ocr` TRAVELS WITH IT TOO, FOR THE SAME REASON ONE STEP ON. The refusal says WHAT was
     * decided; the provenance says WHO did the reading it was decided from, and `attestable` needs
     * both because the same verdict means opposite things over a text layer and over a page of
     * pixels (see the section header). A refusal that dropped it would ask the admin to accept a
     * judgement made out of watermark noise.
     */
    | {
          status: 'rejected';
          reason: RejectionReason;
          unreadable?: UnreadableReason;
          ocr?: true;
          startsOn?: string;
          expiresOn?: string;
          match?: VehicleMatch;
      }
    /**
     * NO PERIOD CAME BACK. `detail` is the PARSER's own reason where the parser is the one that
     * ended the read, and it is carried for exactly one distinction the admin can act on:
     * 'not-a-statutory-certificate' means the file is the wrong KIND of document, which is a
     * different sentence from a certificate nobody could read.
     *
     * `ocr` IS THE SAME PROVENANCE THE `found` READ CARRIES, ON THE END WHERE IT MATTERS MOST. A
     * parser verdict given over OCR text is a verdict given over a guess at the page, and the save
     * guards hold this raw shape rather than the decided one — so the flag has to be here or the
     * two doors would answer the same document differently.
     */
    | {
          status: 'unreadable';
          reason: UnreadableReason;
          detail?: CoverPeriodReason;
          ocr?: true;
      };

/**
 * Is this certificate this contract's certificate?
 *
 * A CERTIFICATE THAT CANNOT BE CHECKED IS NOW REFUSED, WHICH REVERSES THE RULE THIS FUNCTION
 * SHIPPED WITH (owner's ruling, 2026-09-12: "'A certificate that cannot be read, or prints no
 * identifier, is not refused' — I think they should be refused"). The argument that stood before
 * was that a rule refusing what it cannot see is a rule nobody can clear. It is answered by what
 * the slot is FOR: the certificate is the evidence that this car is on cover, and a document the
 * tab cannot read is not evidence of anything — it is a file. There is always a way to clear this
 * one, and it is the same way every time: file the insurer's own PDF certificate. So only a
 * VERIFIED document passes — one that reads as a statutory certificate, prints an identifier, and
 * agrees with the vehicle this contract is written over.
 *
 * IMAGES ARE REFUSED AS A CONSEQUENCE, AND THAT IS THE INTENDED READING. A JPEG or a PNG of a
 * certificate has no text layer, so it ends 'not-pdf' at the picker or 'no-text' at the parser and
 * comes back rejected either way — the first under its own reason, because "this is an image, not
 * a PDF" is a thing the admin can act on without leaving their desk and "nobody could read this"
 * is not. The upload rows still ACCEPT `.jpg`/`.png`, so an image can be chosen
 * and is answered with the sentence rather than silently filtered out of the file picker: narrowing
 * `accept` is a separate call, and a picker that cannot see the file the admin is looking at
 * teaches nothing.
 *
 * THE REJECTION STILL SITS ON THE CERTIFICATE SLOT AND NOT ON `validatePolicyDraft`, WHICH IS THE
 * PLACEMENT RULING AND IS UNCHANGED. That validator gates EVERY save of the insurance group — the
 * insured value and the certificate upload with the dates — so a refusal there would lock all of
 * it behind a document the group cannot fix: on two of the three live mismatches the thing that
 * needs correcting is `vehicles.vin`, which is edited two areas away on the Vehicle block. The
 * docblock at `policyDraft.ts:31-37` records the same argument being accepted once before, about a
 * backwards period. So the refusal is about the DOCUMENT, it is raised where the document is
 * chosen, and a policy whose paper will not pass stays saveable with the certificate left out.
 *
 * WHICH SIDE IS SILENT DECIDES BETWEEN THE TWO 'unverified' REASONS, and the certificate is asked
 * first because its silence is the one the admin cannot fix on this page. A certificate printing
 * NEITHER identifier is 'no-identifier' — a new document is the only way on. Anything else that
 * comes back unverified printed at least one identifier the vehicle record could not meet, and
 * that is 'vehicle-unrecorded', whose imperative — record them on the vehicle — is the fix in both
 * of its shapes: a vehicle holding nothing at all (105 of 289 have no registration row), and one
 * holding only the OTHER identifier. `certificateRefusal` names only what is actually missing, so
 * the second shape is not told it has nothing on file when it has a chassis.
 *
 * PURE: it is handed the read and the vehicle and returns the read it should have been.
 */
export function decideCertificate(read: CoverRead, vehicle: ContractVehicle): CoverRead {
    if (read.status === 'reading' || read.status === 'rejected') return read;

    if (read.status === 'unreadable') {
        // THE RAW ENDING TRAVELS WITH THE FOLDED REFUSAL — see `CoverRead`. The sentence is decided
        // by `reason`; whether there is a door out of it is decided by what the read actually did
        // and by WHICH READER did it, so the provenance rides across too.
        return {
            status: 'rejected',
            reason: rejectionFor(read.reason, read.detail),
            unreadable: read.reason,
            ...provenance(read),
        };
    }

    const match = matchCertificateVehicle(read.vehicle ?? {}, vehicle);
    const period = { startsOn: read.startsOn, expiresOn: read.expiresOn };

    if (match.verdict === 'match') {
        if (read.ocr !== true || match.registration === 'match') return read;
        /*
         * ── UNDER OCR PROVENANCE THE PLATE IS THE GATE ───────────────────────
         *
         * `matchCertificateVehicle` answers `match` as soon as ONE field compares and none differ,
         * which is the right rule for a text layer and too generous for a scan. A plate OCR read as
         * `G5 234-24` fails `PLATE_SHAPE` (`certificateDates.ts:1058`) and is therefore recorded
         * ABSENT rather than mismatched — so a document naming ANOTHER CAR whose chassis happens to
         * agree would come back `match` and file itself with the wrong period. Nothing downstream
         * could catch it: the dates would be internally consistent and a year long.
         *
         * So a read off a scan is accepted only where the REGISTRATION compared. It is the stronger
         * identifier by measurement — 39 of 39 plates agree in the production corpus while 3 of 39
         * chassis numbers do not — it is printed as statutory field 1 in the largest type on the
         * page, and it is the one an admin can check by eye against the car.
         *
         * THE REGISTRATION IS THE ONLY FIELD THAT CAN BE THE PROBLEM HERE, and it is `absent`
         * rather than `mismatch` by construction: a plate that DISAGREED would have made the whole
         * verdict a mismatch. So the reason turns on which side went quiet, exactly as it does for
         * an unverified read below — the certificate printed no plate this parser could recognise,
         * or the vehicle record holds none to compare it with.
         */
        return {
            status: 'rejected',
            reason:
                normaliseIdentifier(match.printed.registration) === ''
                    ? 'no-identifier'
                    : 'vehicle-unrecorded',
            ...period,
            match,
            ...provenance(read),
        };
    }

    if (match.verdict === 'mismatch') {
        return { status: 'rejected', reason: 'mismatch', ...period, match, ...provenance(read) };
    }

    const printedNothing =
        match.printed.registration === undefined && match.printed.chassis === undefined;
    return {
        status: 'rejected',
        reason: printedNothing ? 'no-identifier' : 'vehicle-unrecorded',
        ...period,
        match,
        ...provenance(read),
    };
}

/**
 * WHICH READER PRODUCED THIS, carried onto the refusal it became.
 *
 * ONE AUTHOR BECAUSE THERE ARE FIVE WAYS OUT OF `decideCertificate` AND A FLAG DROPPED ON ONE OF
 * THEM IS A SILENT RULE CHANGE. `attestable` reads the provenance off the decided shape, so a
 * branch that forgot to copy it would quietly close the door on one refusal and leave it open on
 * the next — with nothing to see in the sentence, the fields, or the breadcrumb.
 *
 * SPREAD RATHER THAN SET, so `ocr` is ABSENT on a text-layer read instead of present-and-false.
 * That is the shape the `found` read already uses and the one every equality assertion in the
 * tests is written against.
 */
function provenance(read: CoverRead): { ocr?: true } {
    return 'ocr' in read && read.ocr === true ? { ocr: true } : {};
}

/**
 * WHICH REFUSAL AN UNREADABLE ENDING IS, in the one place the mapping lives.
 *
 * THREE OF THE TEN ENDINGS EARN THEIR OWN SENTENCE AND THE REST SHARE ONE, and the test is whether
 * naming the ending changes what there is to go and do. An image and a file over the reader's
 * ceiling both do: the document on the desk is very likely the right one, and the fix is the
 * insurer's PDF or a smaller scan of it rather than a trip back to the insurer. 'not-a-statutory-
 * certificate' does too — the parser is saying this is the wrong KIND of paper. A scan with no text
 * layer, a PDF pdfjs threw on, a parser that found no period it trusts, and the four ways the OCR
 * pass ends without text are one fact to an admin: nobody can read this.
 *
 * WHICH IS ALSO WHY THE FOUR OCR ENDINGS NEEDED NO CLAUSE OF THEIR OWN HERE. They are not
 * 'not-pdf', they are not 'too-large', and none of them carries a parser `detail` — so all four
 * fall to 'unreadable' through the line that was already written, and `attestable` admits them the
 * same day for the same reason.
 *
 * AN OCR PASS THAT PRODUCED TEXT AND WAS THEN REFUSED BY THE GATE STILL LANDS ON 'not-statutory'
 * HERE, AND THAT IS THE RIGHT SENTENCE FOR IT: the admin is looking at a scan of something this
 * parser does not recognise as a certificate under the Act, and the fix is the same paper it
 * always was. What differs from a text layer saying it is not the WORDS but the DOOR — see
 * `attestable`, which reads the provenance the refusal now carries. This mapping is about the
 * sentence and stays blind to it.
 */
function rejectionFor(reason: UnreadableReason, detail?: CoverPeriodReason): RejectionReason {
    if (reason === 'not-pdf') return 'not-pdf';
    if (reason === 'too-large') return 'too-large';
    return detail === 'not-a-statutory-certificate' ? 'not-statutory' : 'unreadable';
}

/** The typographic apostrophe, which is the canvas convention for prose the admin reads. */
const APOSTROPHE = '’';

/**
 * THE CLOSING IMPERATIVE TURNS ON THE DOOR, WHOLE, AND NOT ONE CLAUSE OF IT — the rule
 * `certificateVehicle`'s own `tailFor` established and the reason it is a whole tail there too.
 *
 * A document being CHOSEN can still be refused, and saying so is the point of the staged tail. One
 * already IN STORAGE cannot be un-filed by a sentence: telling an admin that a certificate they
 * are looking at "cannot be filed" when it demonstrably was is the kind of line that teaches people
 * to ignore the rest of it.
 *
 * AND THE ON-FILE HALF NO LONGER SAYS `Check it against the filed document`, WHICH WAS CIRCULAR ON
 * EVERY REASON THAT REACHES IT HERE. These five refusals are raised precisely because the filed
 * document could not be read, is not a certificate, or prints no identifier — so "check it against
 * the filed document" asks the admin to consult the very paper the sentence has just said says
 * nothing. There is one way out of all five and it is the same one: put the insurer's own PDF
 * certificate on this policy. (The MISMATCH sentence keeps `certificateVehicle`'s tail, because
 * there the filed document reads perfectly and comparing it against the record IS the next move.)
 */
const STAGED_TAIL = ` Upload the insurer${APOSTROPHE}s PDF certificate. It cannot be filed against this contract.`;
const ON_FILE_TAIL = ` File the insurer${APOSTROPHE}s PDF certificate against this policy.`;

/**
 * THE PREFIXES THE REFUSAL MAP MATCHES ON, exported here so each sentence and the thing that
 * recognises it cannot drift apart. Every sentence below opens with one of these (the mismatch
 * sentence opens with `certificateVehicle`'s own `VEHICLE_REFUSAL_PREFIX`), each opening is a
 * phrase nothing else on this canvas says, and no one of them is a prefix of another.
 */
export const UNREADABLE_REFUSAL_PREFIX = 'This certificate could not be read';
export const NOT_PDF_REFUSAL_PREFIX = 'This file is an image, not a PDF certificate';
export const TOO_LARGE_REFUSAL_PREFIX = 'This file is larger than 5 MB';
export const NOT_STATUTORY_REFUSAL_PREFIX = 'This file is not a Certificate of Insurance';
export const NO_IDENTIFIER_REFUSAL_PREFIX = 'This certificate prints no registration';
export const VEHICLE_UNRECORDED_REFUSAL_PREFIX = 'This vehicle has no ';

/**
 * THE ONE SENTENCE ABOUT A VEHICLE RECORD WITH NOTHING ON IT, AND IT HAS ONE AUTHOR BECAUSE IT IS
 * SAID FROM TWO PLACES. `certificateRefusal` says it about a document that READ and printed an
 * identifier the record could not meet; `ATTEST_VEHICLE_UNRECORDED` says it where a document
 * nobody could read has no plate on this contract to be confirmed against. Same missing fact, same
 * fix, same words — and the imperative agrees with the count either way.
 */
function vehicleUnrecordedRefusal(missing: string[]): string {
    const names = missing.join(' or ');
    return (
        `This vehicle has no ${names} on file to check the certificate against. ` +
        `Record ${missing.length > 1 ? 'them' : 'it'} on the vehicle first.`
    );
}

/**
 * The one sentence a rejected certificate ever says, whichever of the seven reasons it carries.
 *
 * ONE AUTHOR FOR THE WHOLE FAMILY, WHICH IS WHY THE MISMATCH CASE DELEGATES RATHER THAN REPEATING.
 * `vehicleRefusal` names the two identifiers that disagree and nothing else can write that
 * sentence; five of the other six are constants but for the one field list below, and `cannotFile`
 * governs every closing imperative the same way, WHOLE.
 *
 * THE IMAGE AND THE OVERSIZE FILE KEEP THEIR OWN OPENINGS, though both are still refused. They are
 * the two endings where the document itself is very likely the right one and the admin can fix it
 * without leaving their desk — export the PDF instead of the photo, or fetch the insurer's original
 * instead of the high-resolution scan — and the generic "could not be read" would hide that.
 *
 * `vehicle-unrecorded` NAMES ONLY WHAT IS ACTUALLY MISSING, and reads `Record them` / `Record it`
 * accordingly. The ruled sentence is the both-ends case, which is the common one; a vehicle holding
 * one identifier and meeting a certificate that printed the other is told that, rather than told it
 * has nothing on file while its chassis sits on the Vehicle block. It takes no door tail: the fix
 * is on the vehicle record either way, and neither filing nor re-reading the document changes it.
 *
 * AND IT ASKS `normaliseIdentifier`, NOT `=== undefined`. `matchCertificateVehicle` records a
 * PLACEHOLDER plate (`-`, `.`, a run of spaces) in `expected` because it is not blank, but
 * `compare` normalises it to '' and answers `absent` — so a raw-undefined test would leave that
 * identifier out of `missing` and claim a registration is on file that nothing can be checked
 * against. Asking the same normalisation the comparison asked is what keeps the two in step, and
 * what stops `This vehicle has no  on file` when BOTH values are placeholders.
 */
export function certificateRefusal(
    read: Extract<CoverRead, { status: 'rejected' }>,
    cannotFile = false,
): string {
    const tail = cannotFile ? STAGED_TAIL : ON_FILE_TAIL;

    switch (read.reason) {
        case 'unreadable':
            return (
                'This certificate could not be read, so it cannot be checked against the ' +
                `vehicle.${tail}`
            );
        case 'not-pdf':
            return (
                'This file is an image, not a PDF certificate, so it cannot be read or checked ' +
                `against the vehicle.${tail}`
            );
        case 'too-large':
            return `This file is larger than 5 MB, so it cannot be read here.${tail}`;
        case 'not-statutory':
            return (
                'This file is not a Certificate of Insurance under the Motor Vehicles ' +
                `(Third Party Insurance) Act.${tail}`
            );
        case 'no-identifier': {
            /*
             * IT NAMES ONLY WHAT THE DOCUMENT ACTUALLY FAILED TO PRINT, exactly as the vehicle
             * sentence below names only what the record actually lacks — and for the same reason.
             * On a text layer this reason is raised only where BOTH identifiers are missing, and
             * the list is then both of them and the sentence is word for word what it always was.
             * Under OCR it is also raised where the scan printed a chassis this parser could read
             * and a registration it could not (`decideCertificate`), and there the old wording
             * would have asserted that a chassis number sitting in front of the admin was not
             * there — which is how a refusal teaches people to stop believing refusals.
             *
             * AND THE SENTENCE IS BUILT OUT OF THE PREFIX ITSELF, WHICH IS WHAT KEEPS THE TWO FROM
             * DRIFTING. `refusalFields` matches `NO_IDENTIFIER_REFUSAL_PREFIX` to attribute this
             * refusal to the certificate row, and a refusal that matches no prefix loses its field
             * with no error to show for it. Assembling the clause from a list happened to open on
             * the registration for every shape reachable today; writing the prefix down as the
             * opening makes it so for every shape there will ever be.
             *
             * IT IS TRUE OF BOTH PLACES THIS REASON IS RAISED, which is the fact that licenses the
             * shape: `decideCertificate` raises 'no-identifier' where the certificate printed
             * NEITHER identifier, and where a scan printed no registration this parser could read.
             * The registration is missing in both. Only the chassis varies, so only it is a clause.
             */
            const chassisToo =
                normaliseIdentifier(read.match?.printed.chassis) === '' ? ' or chassis number' : '';
            return (
                `${NO_IDENTIFIER_REFUSAL_PREFIX}${chassisToo}, so it cannot be ` +
                `checked against the vehicle.${tail}`
            );
        }
        case 'vehicle-unrecorded': {
            const missing: string[] = [];
            if (normaliseIdentifier(read.match?.expected.plate) === '') {
                missing.push('registration number');
            }
            if (normaliseIdentifier(read.match?.expected.vin) === '') {
                missing.push('chassis number');
            }
            return vehicleUnrecordedRefusal(missing);
        }
        case 'mismatch':
            /*
             * THE FALLBACK IS A SENTENCE AND NEVER '', because the empty string is the one answer
             * this function must not give: `savePolicyDetails` and the policy dossier's
             * `fileCertificate` raise what comes back as `new Error(...)`, and an Error with no
             * message is a refused save that says nothing
             * at all and attributes to no field. `decideCertificate` is the only author of a
             * 'mismatch' read and always carries the `match` that raised it, so this is unreachable
             * by construction — but the type admits it, and a missing comparison IS a document that
             * could not be checked, which is exactly what the unreadable sentence says. A THROW
             * would be the other honest answer and is rejected: both surfaces call this DURING
             * RENDER, so a throw here takes the whole area down over a can't-happen.
             */
            return read.match
                ? vehicleRefusal(read.match, cannotFile)
                : certificateRefusal({ ...read, reason: 'unreadable' }, cannotFile);
    }
}

/*
 * ── A SCAN NOBODY CAN READ MAY BE FILED ON AN ADMIN'S READING ────────────────
 *
 * THE REFUSAL ABOVE HAS EXACTLY ONE DOOR OUT OF IT, AND THIS IS IT (owner's ruling, 2026-09-17:
 * "OCR, then attested fallback"). A certificate that is a photograph of a page — one A4 sheet made
 * of JPEGs, no text layer, produced by a print-to-PDF driver — is the ordinary statutory form the
 * parser was cut against, printed by the same insurer, naming the same car. Nothing here can read
 * it, and until the reader learns to, "file the insurer's PDF certificate" is advice with nowhere
 * to go: that IS the insurer's PDF certificate. So the admin may file it by stating, on the
 * record, what the document says.
 *
 * IT IS OFFERED ON A DOCUMENT NOTHING READ, AND NOWHERE ELSE, WHICH IS THE WHOLE OF THE SAFETY
 * RULE (owner's ruling: "only on an unreadable end — `no-text` today"). A document that was READ
 * and JUDGED — one naming another car, one that is not a Certificate of Insurance (contract 119
 * has a loan agreement filed as one), one printing no identifier at all, an image where a PDF was
 * wanted, a file over the reader's ceiling, a vehicle record with nothing to meet it with — is
 * refused on evidence, and an attestation over evidence is not a fallback but an override.
 *
 * AND SINCE THE SCAN BRANCH LANDED, "READ" IS A QUESTION ABOUT THE READER AND NOT ONLY ABOUT THE
 * VERDICT (ruling, 2026-09-17, on the owner's "if it still cannot be read"). A text layer is what
 * the insurer's own software wrote into the file; OCR output is this app's guess at what some
 * pixels probably say, and Gate 0 measured exactly what that produces on a page with a watermark
 * across it — runs of noise that carry no Act title and no plate. So the SAME verdict divides:
 *
 *   'not-statutory' or 'no-identifier' over a TEXT LAYER  → the document was read and judged, and
 *       the judgement is the insurer's own words. No door: contract 119's loan agreement and
 *       contract 288's sticker are exactly this, and they are what the rule was written to refuse.
 *   'not-statutory' or 'no-identifier' over OCR TEXT      → the gates refused a guess. G1/G3 run
 *       before any date work (`certificateDates.ts`), so failing them means the pass did not even
 *       recover the Act title — which is the scan still not being readable, said one gate later.
 *       The door is open, and the admin's word is about the paper rather than about the guess.
 *
 * WHAT DOES NOT DIVIDE IS 'mismatch', UNDER ANY PROVENANCE, and it is the one exclusion worth
 * stating on its own. A plate that DISAGREES is not a gate coming back empty: something was read
 * off that page and it names another car, and that is the single case a wrong-document filing
 * would look like. Nor do 'none' and 'ambiguous' divide, which is the same rule seen from the
 * other side: both are reached only PAST G1–G3, so the pass recovered the Act title and both
 * statutory labels and the document genuinely was read — it is the values that could not be
 * pinned. 'failed', 'not-pdf', 'too-large' and 'vehicle-unrecorded' never had a door and still do
 * not: the first three are a file that will not open, and the last is a gap in this app's own
 * vehicle record with nothing for the admin to agree with.
 *
 * WHICH IS NARROWER THAN THE SENTENCE, AND DELIBERATELY SO. `rejectionFor` folds EIGHT endings into
 * `This certificate could not be read…`: a PDF with no text layer, a pdfjs throw, the four ways the
 * OCR pass comes back with nothing, and the parser's own 'none'/'ambiguous' — and those last two
 * are returned only for a document that HAD a text layer, which the parser extracted and judged (a
 * period running backwards, two printings of it disagreeing, a span no cover has). Offering an
 * override there would be an override over exactly the evidence the gate exists to respect, so
 * `ATTESTABLE_ENDINGS` lists the endings rather than the refusal they fold into. The cost is one
 * sentence with two affordances; the alternative is a door opening onto seven parser verdicts, and
 * the ruling chose this side of it.
 *
 * A RASTER STICKER IS AN UNREADABLE END, NOT A JUDGED ONE, and the door is open on it by
 * construction: a picture of a sticker in a PDF has no text layer, so it ends `no-text` like any
 * other scan and nothing here can tell the two apart. What stops it being filed as this contract's
 * cover is the plate and the admin's word about the page, which is the same thing that stops any
 * other scan. (Contract 288's filed document is the live example, and which ending it actually
 * takes has not been measured — see the PR notes.)
 *
 * THE PLATE IS THE GATE, AND IT IS COMPARED WITHOUT FOLDING. The admin types the registration off
 * the document and it must normalise equal to the contract's own — `normaliseIdentifier`, which is
 * uppercase, spaces, hyphens and dots and NOTHING ELSE. O↔0 and I↔1 folding stays absent here for
 * the same measured reason it is absent in `certificateVehicle`: the three real disagreements in
 * production are each one such character, so a folding comparison would wave through exactly the
 * wrong-document case this gate exists to catch. `G7 4821 25` is not `GT 4821 25`.
 *
 * AND THE SENTENCE THAT REFUSES A TYPED PLATE NEVER PRINTS THE EXPECTED ONE. A refusal that
 * answers "that is not it" with "it is GT 4821 25" has turned the gate into a form that fills
 * itself in, and the admin can then confirm a document they never looked at. The checkbox label
 * may name the plate, because by then the two agree and the claim being made is about the paper.
 *
 * NOTHING HERE IS A SECOND PAIR OF DATE FIELDS. The cover dates an attestation carries are the
 * ones already on the block — the same two inputs the read would have prefilled — so an attested
 * filing lands the admin's own reading in the same columns a parsed one would, and there is no
 * second period anywhere that could disagree with the first.
 */

/**
 * What an admin typed to file a certificate nobody here could read.
 *
 * THE `File` IS THE SUBJECT AND ITS IDENTITY IS THE STALENESS KEY, exactly as it is for
 * `PrefillState`: every pick makes one File object, so Replace, Remove and Discard un-arm the
 * attestation for free and it can never outlive the document it speaks for.
 */
export interface Attestation {
    /** The document the admin was looking at when they confirmed it. */
    file: File;
    /** The registration as the admin read it off the certificate, verbatim. */
    plate: string;
    /** The two cover fields as they stood the instant the box was ticked. */
    startsAt: string;
    expiresAt: string;
}

/**
 * THE ENDINGS A DOCUMENT NOBODY READ ACTUALLY ARRIVES ON, which is the list the door is cut to.
 *
 * IT OPENS ON THE SCAN: a PDF whose text layer holds fewer than `MIN_CHARS` characters
 * (`certificateRead.ts`), which is what a photograph of a page is. 'failed' is NOT here — a pdfjs
 * throw is an encrypted, corrupt or mislabelled file, and the honest answer to those is a file
 * that opens, not an admin's word over one that does not. 'none' and 'ambiguous' are not here
 * because they are the PARSER's verdicts, reached only through a text layer it read.
 *
 * AND THE OCR ENDINGS JOIN IT WITH NOTHING ELSE MOVING, exactly as this list was written to let
 * them. `ocr-no-text`, `ocr-timeout`, `ocr-failed` and `ocr-too-many-pages` are the same fact as
 * `no-text` — every reader this tab has was pointed at the document and none of them could read it
 * — so the reader's OCR pass adds four names here and touches no other line of the rule.
 */
const ATTESTABLE_ENDINGS: readonly UnreadableReason[] = [
    'no-text',
    'ocr-no-text',
    'ocr-timeout',
    'ocr-failed',
    'ocr-too-many-pages',
];

/**
 * AND THE TWO A MACHINE READING DOES NOT EARN, which is the second half of the same list.
 *
 * THESE ARE THE GATES REFUSING A GUESS RATHER THAN REFUSING A DOCUMENT. Both are raised before any
 * date is looked at — G1/G3 in `readCoverPeriod`, and the identifier comparison in
 * `decideCertificate` — so over OCR text they say the pass did not recover the Act title, or did
 * not recover a plate this parser can recognise. That is the scan still being unreadable, one gate
 * further on, and the door stays open on it. Over a TEXT LAYER the identical pair is the insurer's
 * own words being read and judged, and the door is shut: see the section header for the split.
 *
 * 'unreadable' IS DELIBERATELY NOT HERE. The endings that fold into it divide into two kinds and
 * `ATTESTABLE_ENDINGS` already names the kind that qualifies — listing the folded refusal instead
 * would hand the door to 'failed', 'none' and 'ambiguous' as a side effect.
 */
const OCR_ATTESTABLE_REASONS: readonly RejectionReason[] = ['not-statutory', 'no-identifier'];

/**
 * MAY THIS READ BE FILED ON AN ATTESTATION?
 *
 * TRUE ONLY WHERE NOTHING READ THE DOCUMENT AT ALL, and false everywhere something read it and
 * judged it. The predicate is in two arms because "read it" turns on the reader as well as on the
 * verdict — see the section header for the ruling and `ATTESTABLE_ENDINGS` /
 * `OCR_ATTESTABLE_REASONS` for the two lists themselves:
 *
 *   1. THE ENDING SAYS NOBODY GOT AS FAR AS TEXT — a scan with no layer, or any of the four ways
 *      the OCR pass comes back with nothing. True under either provenance, because there is no
 *      reading to be had from either.
 *   2. THE PROVENANCE IS OCR AND THE VERDICT IS ONE OF THE PRE-DATE GATES. True only there, and
 *      the same verdict over a text layer is false, which is the whole point of carrying `ocr`
 *      across `decideCertificate` at all.
 *
 * `mismatch` REACHES NEITHER ARM AND THAT IS NOT AN ACCIDENT OF THE LISTS. It is not an ending in
 * the first and it is kept out of the second by name, so no provenance opens it.
 *
 * IT TAKES A READ FROM EITHER SIDE OF `decideCertificate`. The surfaces hold the DECIDED read in
 * `PrefillState`; `certificateAccepted` decides the raw one it has just re-taken and asks about
 * THAT, so both doors ask this of the same shape. The raw arm below is what the standing-line rule
 * and the tests reach, and it answers identically — which is what the decided read's `unreadable`
 * field and its `ocr` flag are both for.
 */
export function attestable(read: CoverRead | null | undefined): boolean {
    if (!read) return false;
    if (read.status === 'rejected') {
        if (read.unreadable !== undefined && ATTESTABLE_ENDINGS.includes(read.unreadable)) {
            return true;
        }
        return read.ocr === true && OCR_ATTESTABLE_REASONS.includes(read.reason);
    }
    if (read.status === 'unreadable') {
        if (ATTESTABLE_ENDINGS.includes(read.reason)) return true;
        // 'no-identifier' is unreachable from this shape — it is raised only about a read that
        // FOUND a period — so the raw arm can only ever gain 'not-statutory' here. Asking the one
        // list rather than that one member is what keeps the two arms the same rule.
        return (
            read.ocr === true &&
            OCR_ATTESTABLE_REASONS.includes(rejectionFor(read.reason, read.detail))
        );
    }
    return false;
}

/**
 * DID THE READ END ON `This certificate could not be read…`, whatever put it there?
 *
 * THIS IS THE WIDE CLASS AND IT ANSWERS A DIFFERENT QUESTION FROM `attestable`. That one decides
 * whether a door may be OPENED; this one decides whether a filing already made through it still
 * STANDS — and the only thing that can retire an attestation is the document becoming readable or
 * being judged. A filed scan whose later re-read throws, or comes back as a text layer the parser
 * cannot pin, is still a document this tab cannot read: printing a refusal over it would be the
 * exact trap the two columns exist to close, and the admin who filed it could do nothing about it.
 *
 * AND IT SUBSUMES `attestable` BY CONSTRUCTION RATHER THAN BY AGREEMENT, WHICH IS THE INVARIANT
 * THAT KEPT THE OFFER HONEST BEFORE PROVENANCE AND NOW HAS TO BE SPELT OUT. A door a filing may be
 * MADE through must be one the filing STANDS on, or the app would take the admin's word and retire
 * it on the very same read. Before the scan branch this held for free — every attestable ending
 * folded into 'unreadable' — but an OCR gate refusal lands on 'not-statutory' or 'no-identifier',
 * which this line reads as judged. It is not judged: the gate refused a guess at some pixels, and
 * `Check again` spending a fresh OCR pass over an already-filed certificate is EXACTLY where that
 * would bite, printing `This file is not a Certificate of Insurance…` over a document an admin was
 * authorised to file. So the narrow rule is asked first and the wide one only adds to it.
 */
function unreadableEnd(read: CoverRead | null | undefined): boolean {
    if (!read) return false;
    if (attestable(read)) return true;
    if (read.status === 'rejected') return read.reason === 'unreadable';
    if (read.status === 'unreadable') return rejectionFor(read.reason, read.detail) === 'unreadable';
    return false;
}

/**
 * IS THERE ANYTHING ON THIS CONTRACT FOR THE ADMIN TO AGREE WITH? Both surfaces ask this rather
 * than `attestable` alone, and it is the difference between a door and a dead end.
 *
 * A VEHICLE WITH NO REGISTRATION ROW — 105 of 289 have none — CANNOT ARM AN ATTESTATION, because
 * `attestedPlateAgrees` treats an absent side as no agreement and always will. Drawn anyway, the
 * plate row refuses every value typed into it with `That is not the registration on this
 * contract's vehicle. Check the certificate.` — which is false twice over: there is no
 * registration on this contract's vehicle, and the certificate is not what is wrong. So the offer
 * is withheld where it could never be taken, and `ATTEST_VEHICLE_UNRECORDED` is what stands in its
 * place: the vehicle record is the thing to go and fix.
 */
export function attestOfferable(
    read: CoverRead | null | undefined,
    vehicle: ContractVehicle,
): boolean {
    return attestable(read) && normaliseIdentifier(vehicle.plate) !== '';
}

/**
 * Does the typed registration agree with the one the contract's vehicle holds?
 *
 * EXPORTED BECAUSE THE DISAGREEMENT SENTENCE AND THE ARMING RULE MUST BE ONE RULE. A surface that
 * decided on its own when to print `That is not the registration…` would be a second comparison
 * able to drift from the one that actually arms the checkbox.
 *
 * AN ABSENT SIDE IS NOT AGREEMENT. A vehicle with no registration row — 105 of 289 have none — has
 * nothing for the admin to agree WITH, so the attestation simply cannot be armed there and the
 * plate row says so by refusing every value typed into it.
 */
export function attestedPlateAgrees(typed: string, held: string | null | undefined): boolean {
    const a = normaliseIdentifier(typed);
    const b = normaliseIdentifier(held);
    return a !== '' && b !== '' && a === b;
}

/**
 * IS THE ATTESTATION STANDING RIGHT NOW? Asked at render by both surfaces and again by both save
 * guards, over the same five conditions.
 *
 * ANY EDIT UN-TICKS IT, and that is what the last two checks are: the confirmation is about a
 * PARTICULAR document and a PARTICULAR period, so a file replaced (`attested.file !== staged`) or
 * a date typed over (`current` no longer matching what was confirmed) retires it rather than
 * carrying the admin's word onto something they did not read. A plate edited after the tick is
 * retired by the surfaces, which drop the whole `Attestation` on the keystroke — there is nothing
 * left here for it to be compared against.
 *
 * BOTH DATES MUST BE PRESENT, because the period is half of what is being attested and a
 * half-filled pair is not a period. `validatePolicyDraft` still has its own say about them.
 */
export function attestationHolds(
    read: CoverRead | null | undefined,
    vehicle: ContractVehicle,
    attested: Attestation | null,
    staged: File | null,
    current: DatePair,
): boolean {
    if (attested === null || staged === null) return false;
    if (attested.file !== staged) return false;
    if (!attestable(read)) return false;
    if (attested.startsAt === '' || attested.expiresAt === '') return false;
    if (current.startsAt !== attested.startsAt) return false;
    if (current.expiresAt !== attested.expiresAt) return false;
    return attestedPlateAgrees(attested.plate, vehicle.plate);
}

/**
 * A save guard's answer: the refusal to raise, or null — and whether what got through got through
 * on the admin's word.
 *
 * A REFUSAL-OR-NULL AND NOT A DISCRIMINATED UNION, which is this repo's own idiom for exactly this
 * shape (`validatePolicyDraft` two lines above the first caller, `fieldErrorsFrom`, every
 * `certificateRefusal` sentence) and is the shape that survives `tsconfig.app.json`: with
 * `strict: false` a `{ ok: true } | { ok: false; refusal: string }` does not narrow on
 * `!verdict.ok`, so the sentence would be unreachable at the type level in both save guards.
 *
 * `attested` IS FALSE WHENEVER `refusal` IS SET. Nothing was filed, so nothing was filed on
 * anybody's reading.
 */
export interface CertificateVerdict {
    /** The sentence to raise, or null where the document may be filed. */
    refusal: string | null;
    /** True only where it passed on the admin's own reading rather than on the reader's. */
    attested: boolean;
}

/**
 * THE ONE AUTHOR OF BOTH SAVE GUARDS. `savePolicyDetails` and the policy dossier's
 * `fileCertificate` ask this of the bytes they are about to upload, and neither decides anything
 * else about the document.
 *
 * THE RE-READ IS THE POINT AND THE ATTESTATION DOES NOT SURVIVE IT CHANGING ITS MIND. A disabled
 * control is a courtesy and component state is not evidence, so the guard re-reads the file it
 * holds — and if THAT read comes back `found`, the bytes under the admin's word are not the bytes
 * they attested to: a readable document has arrived in the slot, and the ordinary rules decide it.
 * `attestationHolds` refuses every non-unreadable read for exactly this reason, so a document that
 * now reads as another car's is refused with the mismatch sentence rather than filed on a word
 * given about a different page.
 *
 * `!== 'rejected'` AND NOT `=== 'found'`, which is the condition both guards already carried
 * verbatim. `decideCertificate` answers a finished read with one of those two and nothing else;
 * spelling the acceptance as the complement keeps this a refactor of the guards rather than a new
 * rule hiding inside one.
 *
 * AND THE ATTESTATION IS ASKED ABOUT THE DECISION, NOT ABOUT THE RAW READ, WHICH IS WHAT KEEPS THE
 * OFFER FROM BEING A TRAP. The surfaces hold the decided read and offer the door off it; this
 * guard holds the raw one. For every ending that never reached a period the two shapes answer
 * `attestable` identically and it made no difference — but a scan whose OCR text FOUND a period
 * and was then refused by a pre-date gate is `found` in the raw shape and `rejected` in the
 * decided one. Asked raw, this guard would refuse a document the row had just invited the admin to
 * confirm, and the only symptom would be a Save that does nothing. So it decides first and asks
 * about that, which is the same question the screen asked.
 */
export function certificateAccepted(
    read: CoverRead,
    vehicle: ContractVehicle,
    attested: Attestation | null,
    staged: File | null,
    current: DatePair,
): CertificateVerdict {
    const decision = decideCertificate(read, vehicle);
    if (decision.status !== 'rejected') return { refusal: null, attested: false };
    if (attestationHolds(decision, vehicle, attested, staged, current)) {
        return { refusal: null, attested: true };
    }
    return { refusal: certificateRefusal(decision, true), attested: false };
}

/**
 * The offer, on the refusals that have a door out of them — see `attestable` for which, and why a
 * gate refusing OCR output is among them while the same gate over a text layer is not.
 *
 * ONE SENTENCE FOR ALL OF THEM, AND IT IS TRUE OF ALL OF THEM. Every ending the door opens on is a
 * document this tab could not read: the pass came back with nothing, or came back with a guess the
 * gates would not take. `This certificate could not be read` is the honest summary of both, and it
 * is the sentence the refusal it replaces was already going to open with on the commonest of them.
 */
export const ATTEST_OFFER = 'This certificate could not be read. File it by confirming what it says.';

/** The plate row's own name. It asks for the DOCUMENT's registration, not the contract's. */
export const ATTEST_PLATE_LABEL = 'Registration on the certificate';

/**
 * THE DISAGREEMENT, AND IT NAMES NOTHING. Printing the expected plate here would turn the gate
 * into a form that fills itself in — see the section header.
 */
export const ATTEST_PLATE_DISAGREES = `That is not the registration on this contract${APOSTROPHE}s vehicle. Check the certificate.`;

/** What stands where the checkbox would be while the period is still half-typed. */
export const ATTEST_DATES_FIRST =
    'Enter the cover dates from the certificate before confirming it.';

/**
 * WHAT STANDS WHERE THE DOOR WOULD BE, ON A VEHICLE THE APP HOLDS NO PLATE FOR. It is the refusal
 * `certificateRefusal` already writes about a vehicle record with nothing on it, word for word —
 * the missing fact is the same one and so is the fix, and a second sentence saying it differently
 * would be a second rule. See `attestOfferable`.
 */
export const ATTEST_VEHICLE_UNRECORDED = vehicleUnrecordedRefusal(['registration number']);

/**
 * The confirmation itself, which states the claim rather than asking for consent to a policy: what
 * is being confirmed is that the admin read this document and it says these two things.
 */
export function attestCheckboxLabel(plate: string, period: string): string {
    return `I have read this certificate: it names ${plate} and runs ${period}.`;
}

/** What a filing says when the profile behind `certificate_attested_by` cannot be named. */
export const UNNAMED_ATTESTOR = 'an admin';

/**
 * AND WHAT THE SAVE SAYS WHEN IT CANNOT FIND OUT WHOSE READING THIS IS.
 *
 * `UNNAMED_ATTESTOR` IS THE ANSWER FOR A PROFILE THAT HAS GONE, AND NOT FOR ONE NEVER RESOLVED.
 * The column is `ON DELETE SET NULL`, so a filing whose admin has since been removed is a real
 * record with a name missing from it. A filing written with no author because the lookup failed at
 * the moment of writing is a different thing wearing the same shape — the record would say an
 * unnamed admin read this document, and nobody could ever find out who — and the ruling this
 * feature exists under is that the record names who and when. So the save refuses instead, before
 * a byte reaches storage, and says what to do about it.
 *
 * IT IS NOT IN `POLICY_REFUSAL_PREFIXES` AND DOES NOT WANT TO BE. Nothing on the block is wrong:
 * not the certificate, not the period, not the value — so it lands on the group line rather than
 * pointing a caret at a field that is already right, which is the placement
 * `Link a policy before recording what was paid for it.` established.
 */
export const ATTEST_PROFILE_UNKNOWN =
    'This filing could not be recorded against your profile, so nothing was filed. ' +
    'Sign out and back in, then try again.';

/**
 * THE STANDING LINE, WHICH IS WHAT MAKES THE OFFER HONEST RATHER THAN A TRAP. Without the two
 * columns behind it, every later view of this policy would re-read the filed scan, fail again, and
 * keep a refusal banner standing forever over a document the admin was authorised to file. The
 * sentence states who filed it and when, and then says plainly what has not changed: the tab still
 * cannot read the paper, and nobody should believe it has been checked.
 *
 * `day` IS INJECTED for the same reason every other date in this module is — the surfaces print it
 * through `businessDay`, on the Ghana calendar day, and this file holds no clock and parses no
 * string.
 */
export function attestedFilingLine(name: string, day: string): string {
    return `Filed on ${name}${APOSTROPHE}s reading of the document on ${day}. Nothing here can read it.`;
}

/** The same fact, three words long, for the head that is read with the block closed. */
export function attestedSummaryTail(name: string): string {
    return `filed on ${name}${APOSTROPHE}s reading`;
}

/** The two attestation columns, exactly as the `insurances` row holds them. */
export interface FiledAttestation {
    /** `insurances.certificate_attested_at` — a timestamptz, or null. */
    attestedAt: string | null;
    /** `insurances.certificate_attested_by` — a `profiles.id` (bigint), or null. */
    attestedBy: number | null;
}

/**
 * IS THE CERTIFICATE ON FILE STANDING ON AN ADMIN'S READING? Both halves are required, and the
 * second is why this is a rule rather than a null check.
 *
 * THE COLUMNS ALONE ARE NOT THE ANSWER. They are cleared whenever a readable certificate is filed
 * over the attested one, but a row that somehow carries both a readable document and a stale
 * attestation must be believed about the DOCUMENT: if the reader can read what is on file, the
 * ordinary rules decide it and the standing line has nothing to stand on. So the read has to be
 * one nothing here can read before the attestation means anything at all.
 *
 * AND IT IS THE WIDE CLASS HERE, NOT THE OFFER'S. `attestable` decides where a filing may be MADE
 * and is cut to the endings on which nothing read the document; this decides whether a filing
 * already made still stands, and the only things that may retire it are the document becoming
 * readable or being judged. See `unreadableEnd`.
 */
export function attestedFiling(
    read: CoverRead | null | undefined,
    columns: FiledAttestation | null | undefined,
): { attestedAt: string; attestedBy: number | null } | null {
    const attestedAt = columns?.attestedAt ?? null;
    if (attestedAt === null || attestedAt === '') return null;
    if (!unreadableEnd(read)) return null;
    return { attestedAt, attestedBy: columns?.attestedBy ?? null };
}

/** What the apply did to the fields: filled empty ones, replaced values, or agreed with them. */
export type ApplyMode = 'filled' | 'replaced' | 'matched';

/** Everything a surface remembers about the certificate currently staged. */
export interface PrefillState {
    /**
     * The File this state is about. IDENTITY IS THE STALENESS KEY: every pick produces exactly one
     * File object, so a caller that renders only while `state.file === staged.file` gets Discard,
     * Remove, Replace and a landed commit clearing the line for free — the line can never outlive
     * the document it describes, and the worst a future refactor can do is stop showing it.
     */
    file: File | null;
    read: CoverRead | null;
    /** Set once, when a found read is applied. Null while reading and on every unreadable end. */
    mode: ApplyMode | null;
    /** The two values as they stood the instant before the apply, or null if nothing was replaced. */
    replaced: DatePair | null;
}

/** No certificate staged: no read, no line. */
export const IDLE_PREFILL: PrefillState = { file: null, read: null, mode: null, replaced: null };

/** What the caller should do with a read that has just resolved. */
export type PrefillDecision =
    | { kind: 'apply'; set: Partial<DatePair>; mode: ApplyMode; replaced: DatePair | null }
    | { kind: 'hold' }
    | { kind: 'drop' };

/**
 * Should this read be applied, held for later, or thrown away?
 *
 * THE ORDER OF THE FIVE RULES IS THE CONTRACT, not an implementation detail, and the case that
 * proves it is a read that is BOTH stale and mid-commit: staleness is terminal (the file it speaks
 * for is gone), so it must be answered before `saving`, which only defers. A held read is retried
 * by the caller on the `saving -> false` transition, which is why a FAILED commit releases it —
 * that is the Save-pressed-on-empty-dates case, where the admin is reading a refusal about these
 * very fields and the certificate is still staged.
 *
 * `savedAt` is compared rather than inspected: the caller captures it when the read starts and
 * passes what it says now. Any movement means a commit landed, the staged file was filed with the
 * dates as typed, and a late apply would re-dirty a just-saved block out of nowhere.
 */
export function decidePrefill(input: {
    /** The file the read was started for. */
    readFor: File;
    /** The file staged right now — a different object means a newer pick, a remove or a discard. */
    staged: File | null;
    savedAtAtStart: string | null;
    savedAtNow: string | null;
    saving: boolean;
    current: DatePair;
    read: { startsOn: string; expiresOn: string };
}): PrefillDecision {
    if (input.staged !== input.readFor) return { kind: 'drop' };
    if (input.savedAtNow !== input.savedAtAtStart) return { kind: 'drop' };
    if (input.saving) return { kind: 'hold' };

    const set: Partial<DatePair> = {};
    if (input.current.startsAt !== input.read.startsOn) set.startsAt = input.read.startsOn;
    if (input.current.expiresAt !== input.read.expiresOn) set.expiresAt = input.read.expiresOn;

    // The document agrees with what the fields already say. Nothing is written, and nothing can be
    // put back — but the read still happened, and the line says so.
    if (Object.keys(set).length === 0) {
        return { kind: 'apply', set: {}, mode: 'matched', replaced: null };
    }

    // ONE non-empty field is enough to make this a replacement. The pair is restored as a pair, so
    // the empty half is remembered as '' and put back as '' — undoing half of an overwrite would
    // leave a period that was never on any document.
    const hadValues = input.current.startsAt !== '' || input.current.expiresAt !== '';
    return {
        kind: 'apply',
        set,
        mode: hadValues ? 'replaced' : 'filled',
        replaced: hadValues ? { ...input.current } : null,
    };
}

/** The sentence under the certificate row, and the single action that flips with its state. */
export type PrefillLine =
    | { tone: 'reading'; text: string }
    | { tone: 'read'; text: string; action: { label: string; kind: 'restore' | 'reapply' } | null }
    | { tone: 'unreadable'; text: string }
    /**
     * A CERTIFICATE THAT IS NOT THIS CONTRACT'S COVER, AND IT CARRIES NO ACTION. Every other tone
     * offers the admin a way to move the dates; this one has nothing to offer, because the fix is
     * not on this row — it is either a document that can be checked or a correction to the vehicle
     * record. The sentence is `certificateRefusal`'s, without the 'cannot be filed' clause: this
     * module is read by the staged doors AND by the on-file advisory, and only the first two may
     * say that.
     */
    | { tone: 'rejected'; text: string }
    | null;

/**
 * THE UNREADABLE TONE IS ON NO PATH AN ADMIN CAN WALK, AND THESE THREE SENTENCES ARE THE BACKSTOP.
 * Since the 2026-09-12 ruling every read that has been through `decideCertificate` comes back
 * `rejected` rather than `unreadable`, and both surfaces decide before they publish — so the only
 * way into this branch is a caller handing the module a RAW `CoverRead`, which the type still
 * admits. The three are kept because a branch falling through to nothing would print an empty line
 * rather than a sentence, and go the day `PrefillState.read` stops admitting an undecided read.
 *
 * THERE IS NO FETCH SENTENCE AMONG THEM, AND THERE IS NO FETCH REASON TO CARRY ONE. A certificate
 * already on file that never came back down is a react-query `isError` and never a `CoverRead`:
 * `FETCH_FAILED_NOTE` is what both surfaces print for it, straight — see `UnreadableReason`.
 */
const UNREADABLE =
    'No cover period could be read from this certificate. Enter the dates from the document.';
const NOT_PDF = 'Dates are read from a PDF only. Enter them from the certificate.';
const TOO_LARGE = 'This certificate is too large to read here. Enter the dates from the document.';
/**
 * EXPORTED, BECAUSE IT IS THE ONE UNREADABLE SENTENCE ANY SURFACE STILL PRINTS. The on-file read is
 * a react-query entry now, so its fetch failure arrives as `isError` rather than as a `CoverRead`,
 * and both surfaces print this line straight. It stays here, with the family it belongs to, rather
 * than being retyped in two components that would then drift.
 */
export const FETCH_FAILED_NOTE =
    'The certificate on file could not be opened for reading. Enter the dates from the document.';
/** The label on the one control a standing read offers. One apostrophe mechanism: `APOSTROPHE`. */
const REAPPLY = `Use the certificate${APOSTROPHE}s dates`;

/**
 * THE WAIT THAT HAS TO EXPLAIN ITSELF. A text layer is read between two frames; a scan is
 * rasterised at 300 dpi, binarised and put through a wasm OCR pass, and from the admin's side a
 * read that is going to take fifteen seconds looks exactly like one that has hung. Naming what is
 * happening is the whole of the answer — there is no progress to report that anyone could act on.
 *
 * `here` IS THE LOAD-BEARING WORD. Nothing about this document is sent anywhere: the render, the
 * OCR and the parse all happen in the tab, which is a fact about a customer's insurance paperwork
 * that an admin is entitled to be told rather than left to assume.
 *
 * EXPORTED BECAUSE THE FILED DOCUMENT WAITS TOO, AND WAITS LONGEST. `Check again` is the one door
 * that deliberately spends an OCR pass on a certificate already in storage, and its own line —
 * 'Checking the certificate on file…' — covers a fetch as well as a parse and so cannot say this.
 * The canvas swaps to this sentence for the duration, which keeps one author for the wording
 * whichever door the scan came through.
 */
export const SCAN_READING = 'This certificate is a scan. Reading it here…';

/**
 * THE CLAUSE EVERY PERIOD READ OFF A SCAN CARRIES, and the only defence against the one error class
 * that is invisible to every check in this module. A single wrong digit inside the plausibility
 * window, on a date whose span is still about a year, produces a period that is internally
 * consistent, correctly shaped, and wrong — `policyPeriodWarning` has nothing to say about it and
 * neither has the vehicle rule. No amount of smart repair fixes that; a human looking at the paper
 * does. So the line says where the dates came from and asks for exactly that.
 */
const CHECK_THE_DOCUMENT = ' Check it against the document.';

/**
 * What to say under the certificate row, given the read and what the fields say RIGHT NOW.
 *
 * THE DRAFT IS RE-READ ON EVERY RENDER, WHICH IS WHAT MAKES THE LINE HONEST. The instant the admin
 * types over a prefilled date the line stops claiming to have filled anything and becomes a
 * standing fact — `The certificate reads …` — with the inverse action. It never nags and never
 * re-prompts: the app has no basis to insist after an edit, and every basis to keep saying what
 * the document says.
 *
 * ONE ACTION, NEVER TWO. A control whose label flips always offers the move that takes you to the
 * other reading and never offers a no-op, so a mismatch is answered by `reapply` even when the
 * old values are still remembered.
 *
 * THE ENDED-COVER SENTENCE IS THE ONLY SIGNAL FOR A CERTIFICATE THAT IS GENUINELY LAST YEAR'S, and
 * it is ADVISORY — it blocks nothing, writes nothing, and the prefill it trails has already landed.
 * `certificateDates` reads exactly one document class and refuses everything that names itself a
 * notice, but two residuals get through it by construction and neither is visible to a parser:
 * (a) last year's GENUINE certificate, which is a statutory certificate and reads correctly; and
 * (b) a renewal letter that reprints fields 3 and 4 in prose the competing vocabulary has never
 * heard. Both hand back a clean, one-year, internally consistent period, so `policyPeriodWarning` —
 * which only ever questions the SPAN — has nothing to say about either. What separates them from a
 * live certificate is not on the page at all: it is today. So whenever the read's expiry is BEFORE
 * today, every 'read' sentence says so in the admin's own calendar, and the admin is the check.
 *
 * BEFORE, NOT ON OR BEFORE. A policy whose last day is today has not ended — cover runs to the end
 * of its expiry day — so the sentence appears the morning after and not a day sooner.
 *
 * `period`, `day` and `todayIso` are all injected — callers pass wrappers over `policyPeriodText`
 * and `businessDay`, and `format(businessToday(), 'yyyy-MM-dd')` for the day — so this module never
 * touches `new Date(str)`, which west of Greenwich renders a 'yyyy-MM-dd' a day early, and never
 * reads a clock that a test would then have to freeze.
 */
export function prefillLine(
    state: PrefillState,
    current: DatePair,
    period: (a: string, b: string) => string,
    /** Today on the Ghana calendar, 'yyyy-MM-dd', from the caller's `businessToday()`. */
    todayIso: string,
    /** One date, printed in `policyPeriodText`'s convention: `Apr 17, 2026`. */
    day: (iso: string) => string,
): PrefillLine {
    if (state.file === null || state.read === null) return null;

    if (state.read.status === 'reading') {
        return {
            tone: 'reading',
            text: state.read.phase === 'ocr' ? SCAN_READING : 'Reading the certificate…',
        };
    }

    /*
     * BEFORE THE DATES, BECAUSE THE DATES ARE NOT THE SUBJECT ANY MORE. A rejected read has a
     * perfectly good period on it and no business prefilling anything: the question the line
     * answers has stopped being "when does this cover run" and become "is this the right piece of
     * paper". Nothing below this point can be reached by a rejected read.
     */
    if (state.read.status === 'rejected') {
        return { tone: 'rejected', text: certificateRefusal(state.read) };
    }

    if (state.read.status === 'unreadable') {
        const reason = state.read.reason;
        const text = reason === 'not-pdf' ? NOT_PDF : reason === 'too-large' ? TOO_LARGE : UNREADABLE;
        return { tone: 'unreadable', text };
    }

    const read = state.read;
    const printed = period(read.startsOn, read.expiresOn);
    /*
     * Both sides are fixed-width 'yyyy-MM-dd', so `<` IS the calendar-day comparison — no Date, no
     * zone, no parse. It trails every 'read' sentence below and none of the other tones: a read
     * still in flight has no period to have ended, and an unreadable one has no date to print.
     */
    const ended = read.expiresOn < todayIso ? ` This cover ended on ${day(read.expiresOn)}.` : '';
    /*
     * THE PERIOD PREFILLS EXACTLY AS A TEXT PDF'S WOULD, AND THE SENTENCE SAYS WHERE IT CAME FROM.
     * Three states, three sentences, unchanged — a scan only changes the noun and adds the clause,
     * because what the admin has to DO about a read is the same either way and what they have to
     * KNOW about it is not.
     */
    const source = read.ocr === true ? 'scan' : 'certificate';
    const check = read.ocr === true ? CHECK_THE_DOCUMENT : '';

    // Checked FIRST, so an edit or an undo always wins over what the apply once was.
    if (current.startsAt !== read.startsOn || current.expiresAt !== read.expiresOn) {
        return {
            tone: 'read',
            text: `The ${source} reads ${printed}.${ended}${check}`,
            action: { label: REAPPLY, kind: 'reapply' },
        };
    }

    // Nothing was written — either the read agreed with the fields, or the state predates an apply.
    if (state.mode === 'matched' || state.mode === null) {
        return {
            tone: 'read',
            text: `The ${source} reads ${printed}, which is what the dates below say.${ended}${check}`,
            action: null,
        };
    }

    const back = state.replaced;
    return {
        tone: 'read',
        text: `Read from the ${source}: ${printed}. Filled in below.${ended}${check}`,
        action: back === null ? null : { label: restoreLabel(back, period), kind: 'restore' },
    };
}

/**
 * `Put back Apr 16, 2026 — Apr 15, 2027`, or `Put back what was there` when there is no period to
 * print.
 *
 * A HALF-FILLED PAIR HAS NO PERIOD. `policyPeriodText` returns null unless BOTH ends are readable,
 * so a replacement of one typed date and one empty field would otherwise offer `Put back ` with
 * nothing after it. The action still restores the pair exactly as it stood; only the label changes.
 *
 * IT NEVER SAYS 'Clear the dates', WHICH WOULD BE A LIE ABOUT THE ONLY STATE THAT REACHES HERE.
 * `replaced` is non-null only when `decidePrefill` saw at least one non-empty field — two empty
 * fields produce mode 'filled' and no restore action at all — so the empty period printed here is
 * always the HALF-filled one, and the undo puts a date back rather than clearing anything.
 */
function restoreLabel(replaced: DatePair, period: (a: string, b: string) => string): string {
    const printed = period(replaced.startsAt, replaced.expiresAt);
    return printed === '' ? 'Put back what was there' : `Put back ${printed}`;
}
